import { useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { toast } from 'sonner';
import { Clock } from 'lucide-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/shared/ui/button';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import { Switch } from '@/shared/ui/switch';
import { Card } from '@/shared/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';
import { fetchAuditRules, saveAuditRules, type AuditRule } from '../api/use-audit';

const schema = z.object({
  enabled: z.boolean(),
  autoBlock: z.boolean(),
  maxTransactions: z.coerce.number().int().min(2, 'Mínimo 2 transações'),
  windowHours: z.coerce.number().int().min(1, 'Mínimo 1 hora'),
  lookbackDays: z.coerce.number().int().min(1, 'Mínimo 1 dia'),
});

type FormValues = z.infer<typeof schema>;

const DEFAULT_VALUES: FormValues = {
  enabled: true,
  autoBlock: true,
  maxTransactions: 2,
  windowHours: 24,
  lookbackDays: 30,
};

function ruleToFormValues(rule: AuditRule): FormValues {
  const params = rule.params ?? ({} as AuditRule['params']);
  return {
    enabled: Boolean(rule.enabled),
    autoBlock: Boolean(rule.autoBlock),
    maxTransactions: params.maxTransactions > 0 ? params.maxTransactions : 2,
    windowHours: params.windowHours > 0 ? params.windowHours : 24,
    lookbackDays: params.lookbackDays > 0 ? params.lookbackDays : 30,
  };
}

interface AuditRulesDialogProps {
  idPassbook: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSaved: () => void;
}

/** Configuração da(s) regra(s) que definem um lead como "suspeito". Hoje só a regra de janela curta. */
export function AuditRulesDialog({ idPassbook, open, onOpenChange, onSaved }: AuditRulesDialogProps) {
  const queryClient = useQueryClient();
  const { data, isLoading, isFetching } = useQuery({
    queryKey: ['audit-rules', idPassbook],
    queryFn: () => fetchAuditRules(idPassbook),
    enabled: open,
    staleTime: 0,
    refetchOnMount: 'always',
  });

  const form = useForm<FormValues>({ resolver: zodResolver(schema), defaultValues: DEFAULT_VALUES });
  const errors = form.formState.errors;
  const enabled = form.watch('enabled');

  useEffect(() => {
    if (!open || !data) return;
    const shortWindow = data.find((r) => r.type === 'short-window');
    form.reset(shortWindow ? ruleToFormValues(shortWindow) : DEFAULT_VALUES);
  }, [open, data, form]);

  const onSubmit = form.handleSubmit(async (values) => {
    const rule: AuditRule = {
      type: 'short-window',
      enabled: values.enabled,
      autoBlock: values.autoBlock,
      params: {
        maxTransactions: values.maxTransactions,
        windowHours: values.windowHours,
        lookbackDays: values.lookbackDays,
      },
    };
    try {
      await saveAuditRules(idPassbook, [rule]);
      queryClient.setQueryData(['audit-rules', idPassbook], [rule]);
      await queryClient.invalidateQueries({ queryKey: ['audit-rules', idPassbook] });
      toast.success('Regras de auditoria salvas.');
      onOpenChange(false);
      onSaved();
    } catch {
      toast.error('Falha ao salvar as regras.');
    }
  });

  const showForm = !isLoading && !(isFetching && !data);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Configurar regras de auditoria</DialogTitle>
          <DialogDescription>Defina o que torna um lead suspeito nesta loja.</DialogDescription>
        </DialogHeader>

        {!showForm ? (
          <p className="text-sm text-muted-foreground">Carregando…</p>
        ) : (
          <form onSubmit={onSubmit} className="space-y-4">
            <Card className="space-y-4 p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-center gap-2">
                  <Clock className="size-4 text-muted-foreground" />
                  <div>
                    <p className="text-sm font-medium">Transações em janela curta</p>
                    <p className="text-xs text-muted-foreground">
                      Marca como suspeito quem fizer várias transações em pouco tempo.
                    </p>
                  </div>
                </div>
                <Controller
                  control={form.control}
                  name="enabled"
                  render={({ field }) => <Switch checked={field.value} onCheckedChange={field.onChange} />}
                />
              </div>

              <div className={enabled ? 'grid gap-4 sm:grid-cols-3' : 'hidden'}>
                <div className="space-y-2">
                  <Label htmlFor="maxTransactions">Nº de transações</Label>
                  <Input id="maxTransactions" type="number" min={2} {...form.register('maxTransactions')} />
                  {errors.maxTransactions ? (
                    <p className="text-xs text-destructive">{errors.maxTransactions.message}</p>
                  ) : null}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="windowHours">Janela (horas)</Label>
                  <Input id="windowHours" type="number" min={1} {...form.register('windowHours')} />
                  {errors.windowHours ? (
                    <p className="text-xs text-destructive">{errors.windowHours.message}</p>
                  ) : null}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="lookbackDays">Analisar últimos (dias)</Label>
                  <Input id="lookbackDays" type="number" min={1} {...form.register('lookbackDays')} />
                  {errors.lookbackDays ? (
                    <p className="text-xs text-destructive">{errors.lookbackDays.message}</p>
                  ) : null}
                </div>
              </div>

              {enabled ? (
                <div className="flex items-center justify-between rounded-xl border border-border p-3">
                  <div>
                    <p className="text-sm font-medium">Bloquear automaticamente</p>
                    <p className="text-xs text-muted-foreground">
                      Sem isso, a regra só lista os suspeitos — o bloqueio continua manual.
                    </p>
                  </div>
                  <Controller
                    control={form.control}
                    name="autoBlock"
                    render={({ field }) => <Switch checked={field.value} onCheckedChange={field.onChange} />}
                  />
                </div>
              ) : null}
            </Card>

            <DialogFooter>
              <Button type="button" variant="secondary" onClick={() => onOpenChange(false)}>
                Cancelar
              </Button>
              <Button type="submit" loading={form.formState.isSubmitting}>
                Salvar regras
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
